import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import IconButton from '@material-ui/core/IconButton';
import Badge from '@material-ui/core/Badge';
import NotificationsIcon from '@material-ui/icons/Notifications';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import Menu from '@material-ui/core/Menu';
import NotificationTabs from './NotificationTabs';
import { readNotification, readAllNotifications } from '~/store/chat/actions';
import { convertTimeStampToMoment } from '~/utils/moment';

// Notification signature
//  {
//    id: 3,
//    type: 'task',
//    title: 'Task #123 completed',
//    read: true,
//    timestamp: '2025-06-20T17:00:00Z'
// }

// Types
// Chat
// Dispatch

export default function NotificationBell() {
  const [anchorEl, setAnchorEl] = useState(null);
  const { t: translate } = useTranslation();
  const notifications = useSelector(state => state.chat.chatNotifications);
  const equipments = useSelector(state => state.manager.equipments);
  const nrefCodeTypes = useSelector(state => state.nref.codeTypes);
  const socket = useSelector(state => state.chat.socketConnection);

  const dispatch = useDispatch();

  useEffect(() => {
    if (socket) {
      socket.emit('request_chat_notifications');
    }
  }, [socket]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const markAsRead = (id) => {
    dispatch(readNotification(id));
    socket.emit('notification_read', { id });
  };

  const markAllAsRead = (idsList) => {
    dispatch(readAllNotifications(idsList));
    socket.emit('notification_read_all', idsList);
  };

  const codeTypeColorIcon = {
    0: '#D32F2F',
    1: '#FF9800',
    2: '#27AE60',
  };

  const dispatchNotifications = notifications.filter(notif => notif.type === 'Dispatch').sort(
    (notifA, notifB) => notifB.timestamp - notifA.timestamp,
  ).map(notif => ({
    ...notif,
    title: (
      <>
        {convertTimeStampToMoment(notif.timestamp).format('HH:mm')}
        <FiberManualRecordIcon
          style={{
            fontSize: 8,
            marginLeft: '8px',
            marginRight: '8px',
          }}
        />
        {translate(`common:${equipments.find(equip => equip.id === notif.equip_id)?.name}`)}
        <ArrowForwardIcon
          style={{
            fontSize: 20,
            marginLeft: '8px',
            marginRight: '8px',
          }}
        />
        <FiberManualRecordIcon
          style={{
            fontSize: 20,
            color: codeTypeColorIcon[notif.code_type],
            marginRight: '8px',
          }}
        />
        {translate(`common:${nrefCodeTypes.find(code => code.id === notif.code_type)?.name}`)}
      </>
    ),
  }));

  const notificationsWithEquipments = notifications.filter(notif => notif.type === 'Chat').sort(
    (notifA, notifB) => notifB.timestamp - notifA.timestamp,
  ).map(
    notif => ({
      ...notif,
      title: (
        <>
          {convertTimeStampToMoment(notif.timestamp).format('HH:mm')}
          <FiberManualRecordIcon
            style={{
              fontSize: 8,
              marginLeft: '8px',
              marginRight: '8px',
            }}
          />
          {translate(`common:${equipments.find(equip => equip.id === notif.equip_id)?.name}`)}
          <ArrowForwardIcon
            style={{
              fontSize: 20,
              marginLeft: '8px',
              marginRight: '8px',
            }}
          />
          {translate('common:SentMessage')}
        </>
      ),
    }),
  );

  const unreadCount = [
    ...dispatchNotifications,
    ...notificationsWithEquipments,
  ].filter(n => !n.read).length;

  const allNotifications = [...dispatchNotifications, ...notificationsWithEquipments];

  return (
    <>
      <IconButton color="inherit" onClick={handleClick}>
        <Badge badgeContent={unreadCount} color="secondary">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        keepMounted
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <NotificationTabs
          notifications={allNotifications}
          markAsRead={markAsRead}
          markAsAllRead={markAllAsRead}
        />
      </Menu>
    </>
  );
}
